import * as React from 'react';
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import { AppBuilderIcon, ChevronDown } from '../../../icons/Icons';
import { ReactComponent as IconAbout } from '../../../images/icons/icon-about.svg';
import { ReactComponent as IconChangelog } from '../../../images/icons/icon-changelog.svg';
import { ReactComponent as IconCustomers } from '../../../images/icons/icon-customers.svg';
import { ReactComponent as IconDocs } from '../../../images/icons/icon-docs.svg';
import { ReactComponent as IconDownload } from '../../../images/icons/icon-download.svg';
import { ReactComponent as IconEnterprise } from '../../../images/icons/icon-enterprise.svg';
import { ReactComponent as IconExplore } from '../../../images/icons/icon-explore.svg';
import { ReactComponent as IconForTeams } from '../../../images/icons/icon-for-teams.svg';
import { ReactComponent as IconIntegrations } from '../../../images/icons/icon-integrations.svg';
import { ReactComponent as IconLearn } from '../../../images/icons/icon-learn.svg';
import { ReactComponent as IconMagic } from '../../../images/icons/icon-magic.svg';
import { ReactComponent as IconNotebooks } from '../../../images/icons/icon-notebooks.svg';
import { ReactComponent as IconBlog } from '../../../images/icons/icon-our-blog.svg';
import { ReactComponent as IconPartners } from '../../../images/icons/icon-partners.svg';
import { ReactComponent as IconPin } from '../../../images/icons/icon-pin.svg';
import { ReactComponent as IconPricing } from '../../../images/icons/icon-pricing.svg';
import { ReactComponent as IconProduct } from '../../../images/icons/icon-product.svg';
import { ReactComponent as IconSemanticModels } from '../../../images/icons/icon-semantic-models.svg';
import { ReactComponent as IconTemplates } from '../../../images/icons/icon-templates.svg';
import { ReactComponent as IconDataScience } from '../../../images/icons/icon-use-case-data-science.svg';
import { ReactComponent as IconExploratoryAnalysis } from '../../../images/icons/icon-use-case-exploratory-analysis.svg';
import { ReactComponent as IconOperationalReporting } from '../../../images/icons/icon-use-case-operational-reporting.svg';
import { ReactComponent as IconSelfServe } from '../../../images/icons/icon-use-case-self-serve.svg';
import { ReactComponent as IconUseCases } from '../../../images/icons/icon-use-cases.svg';
import {
  ANIMATIONS,
  DOCS_LINKS,
  INTERNAL_LINKS,
  LOGIN_LINK,
  SIGNUP_LINK
} from '../../../utils';
import { Button, Heading, Menu } from '../../design-system';

import { NavItem, type TextAlignment } from './NavItem';
import { NavLogo } from './NavLogo';

const Main = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  gap: 3px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin-left: auto;
    justify-content: flex-start;
  }
`;

const Left = styled(Main)``;

const Right = styled(Main)``;

const Misc = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-left: 20px;
  margin-left: auto;
`;

const NavOptions = styled.nav`
  display: flex;
  align-items: center;
  flex: 1 1 auto;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    padding: 0 10px 10px;

    ${Main},
    ${Misc} {
      flex: 1 1 auto;
      width: 100%;
      flex-direction: column;
      padding: 0;
      margin: 0;
      gap: 8px;

      > a {
        margin: 0;
      }
    }

    ${Main} {
      flex: none;
    }
  }
`;

const CenteredNavOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 160px 1fr;

  > * {
    justify-content: center;
  }

  ${Left} {
    justify-content: flex-end;
  }

  ${Right} {
    justify-content: flex-start;
  }
`;

const MiscButton = styled(Button)<{
  $getStarted?: boolean;
  $isMobile?: boolean;
}>`
  && {
    padding: 0 10px;

    ${({ $getStarted }) =>
      $getStarted &&
      css`
        margin-left: 15px;
        padding: 0 15px;
      `}

    ${({ $isMobile }) =>
      $isMobile &&
      css`
        padding: 0 15px;
        color: ${({ theme }) => theme.colors.WHITE};
      `}

      ${({ $isMobile, $getStarted }) =>
      $isMobile &&
      $getStarted &&
      css`
        &&& {
          margin-top: auto;
        }
      `}
  }
`;

const NavItemDropdown = styled.div`
  display: flex;
  position: relative;
`;

const Dropdown = styled(motion.div)`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 8;

  > div {
    gap: 1px;
  }
`;

const ProductDropdown = styled(Dropdown)`
  width: 270px;
`;

const UseCasesDropdown = styled(Dropdown)`
  width: 270px;
`;

const ResourcesDropdown = styled(Dropdown)`
  width: 540px;
`;

const NavDropdownColumns = styled.div`
  display: flex;
  padding: 12px;
  gap: 10px;
`;

const NavDropdownColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 0;

  > div {
    gap: 1px;
  }
`;

const NavDropdownColumnHeader = styled(Heading).attrs({ renderAs: 'h5' })`
  margin-bottom: 8px;
  padding-left: 12px;
  padding-top: 2px;
`;

const NavMenu = styled(Menu)`
  background-color: ${({ theme }) => theme.nav.dropdown.backgroundColor};
`;

interface NavContentsDesktopProps {
  centered?: boolean;
}

export const NavContentsDesktop: React.ComponentType<NavContentsDesktopProps> =
  React.memo(({ centered = false }) => {
    const [isHoveringProduct, setIsHoveringProduct] = useState<boolean>(false);
    const [isHoveringResources, setIsHoveringResources] =
      useState<boolean>(false);
    const [isHoveringUseCases, setIsHoveringUseCases] =
      useState<boolean>(false);

    const navItemProps = {
      fill: false,
      partiallyActive: true,
      align: 'center' as TextAlignment
    };

    const miscButtonProps = {
      $isMobile: false,
      fill: false,
      align: 'center' as TextAlignment
    };

    const productDropdownProps = {
      onMouseEnter: () => setIsHoveringProduct(true),
      onMouseLeave: () => setIsHoveringProduct(false)
    };

    const resourcesDropdownProps = {
      onMouseEnter: () => setIsHoveringResources(true),
      onMouseLeave: () => setIsHoveringResources(false)
    };

    const useCasesDropdownProps = {
      onMouseEnter: () => setIsHoveringUseCases(true),
      onMouseLeave: () => setIsHoveringUseCases(false)
    };

    const productDropdown = (
      <ProductDropdown {...productDropdownProps} {...ANIMATIONS.Popover}>
        <NavMenu>
          <NavItem
            {...navItemProps}
            description="Analyze, model, and explore, like never before"
            icon={<IconNotebooks />}
            text="Notebooks"
            to={INTERNAL_LINKS.Notebooks}
          />
          <NavItem
            {...navItemProps}
            description="Powerful exploration tools in a friendly, no-code UI"
            icon={<IconExplore />}
            text="Explore"
            to={INTERNAL_LINKS.Explore}
          />
          <NavItem
            {...navItemProps}
            description="Smarter, faster analysis"
            icon={<IconMagic />}
            text="Magic AI"
            to={INTERNAL_LINKS.Magic}
          />
          <NavItem
            {...navItemProps}
            description="Seamlessly embed data experiences for your customers"
            icon={<IconDownload />}
            text="Embedded analytics"
            to={INTERNAL_LINKS.EmbeddedAnalytics}
          />
          <NavItem
            {...navItemProps}
            description="Sync governed metrics for consistent insights"
            icon={<IconSemanticModels />}
            text="Semantic Models"
            to={INTERNAL_LINKS.SemanticModels}
          />
          <NavItem
            {...navItemProps}
            description="Bring everyone together in one collaborative workspace"
            icon={<IconForTeams />}
            text="Collaboration and sharing"
            to={INTERNAL_LINKS.ForTeams}
          />
          <NavItem
            {...navItemProps}
            description="Build and share insights with beautiful interactive visualizations"
            icon={<AppBuilderIcon />}
            text="App Builder"
            to={INTERNAL_LINKS.AppBuilder}
          />
          <NavItem
            {...navItemProps}
            description="Connect to anything"
            icon={<IconIntegrations />}
            text="Integrations"
            to={INTERNAL_LINKS.Integrations}
          />
        </NavMenu>
      </ProductDropdown>
    );

    const useCasesDropdown = (
      <UseCasesDropdown {...useCasesDropdownProps} {...ANIMATIONS.Popover}>
        <NavMenu>
          <NavItem
            {...navItemProps}
            description="The most powerful tools for exploration available."
            icon={<IconExploratoryAnalysis />}
            text="Exploratory analysis"
            to={INTERNAL_LINKS.UseCaseExploratoryAnalysis}
          />
          <NavItem
            {...navItemProps}
            description="The first platform actually designed for data scientists."
            icon={<IconDataScience />}
            text="Data science"
            to={INTERNAL_LINKS.UseCaseDataScience}
          />
          <NavItem
            {...navItemProps}
            description="Interactive, intuitive reporting without crazy workarounds."
            icon={<IconOperationalReporting />}
            text="Operational reporting"
            to={INTERNAL_LINKS.UseCaseOperationalReporting}
          />
          <NavItem
            {...navItemProps}
            description="Data tools for stakeholders and decision-makers."
            icon={<IconSelfServe />}
            text="Self-serve"
            to={INTERNAL_LINKS.UseCaseSelfServe}
          />
        </NavMenu>
      </UseCasesDropdown>
    );

    const resourcesDropdown = (
      <ResourcesDropdown {...resourcesDropdownProps} {...ANIMATIONS.Popover}>
        <NavMenu>
          <NavDropdownColumns>
            <NavDropdownColumn>
              <NavDropdownColumnHeader renderAs="h5">
                Get started
              </NavDropdownColumnHeader>
              <NavItem
                {...navItemProps}
                description="Jumpstart with pre-built projects"
                icon={<IconTemplates />}
                text="Templates"
                to={INTERNAL_LINKS.Templates}
              />
              <NavItem
                {...navItemProps}
                description="Video series"
                icon={<IconAbout />}
                isExternal={true}
                text="Hex Foundations"
                to={DOCS_LINKS.HexFoundations}
              />
              <NavItem
                {...navItemProps}
                description="Resources and product guides"
                icon={<IconDocs />}
                isExternal={true}
                text="Docs"
                to={DOCS_LINKS.Home}
              />
              <NavItem
                {...navItemProps}
                description="Product updates"
                icon={<IconChangelog />}
                isExternal={true}
                text="Changelog"
                to={DOCS_LINKS.Changelog}
              />
            </NavDropdownColumn>
            <NavDropdownColumn>
              <NavDropdownColumnHeader renderAs="h5">
                Inspiration
              </NavDropdownColumnHeader>
              <NavItem
                {...navItemProps}
                description="From data teams to data teams"
                icon={<IconBlog />}
                text="Blog"
                to={INTERNAL_LINKS.Blog}
              />
              <NavItem
                {...navItemProps}
                description="Learn and connect with peers"
                icon={<IconPin />}
                text="Resources & Events"
                to={INTERNAL_LINKS.Resources}
              />
              <NavItem
                {...navItemProps}
                description="Empowering the best data teams"
                icon={<IconCustomers />}
                text="Customer stories"
                to={INTERNAL_LINKS.Customers}
              />
              <NavItem
                {...navItemProps}
                description="Learn more about our partnerships"
                icon={<IconPartners />}
                text="Partners"
                to={INTERNAL_LINKS.Partners}
              />
            </NavDropdownColumn>
          </NavDropdownColumns>
        </NavMenu>
      </ResourcesDropdown>
    );

    if (centered) {
      return (
        <CenteredNavOptions>
          <Left>
            <NavItemDropdown>
              <NavItem
                {...productDropdownProps}
                {...navItemProps}
                icon={<IconProduct />}
                isHoverActive={isHoveringProduct}
                text={
                  <>
                    Product <ChevronDown />
                  </>
                }
              />
              <AnimatePresence>
                {isHoveringProduct && productDropdown}
              </AnimatePresence>
            </NavItemDropdown>
            <NavItemDropdown>
              <NavItem
                {...useCasesDropdownProps}
                {...navItemProps}
                icon={<IconUseCases />}
                isHoverActive={isHoveringUseCases}
                text={
                  <>
                    Use cases <ChevronDown />
                  </>
                }
              />
              <AnimatePresence>
                {isHoveringUseCases && useCasesDropdown}
              </AnimatePresence>
            </NavItemDropdown>
            <NavItem
              {...navItemProps}
              icon={<IconEnterprise />}
              text="Enterprise"
              to={INTERNAL_LINKS.Enterprise}
            />
          </Left>
          <NavLogo large={true} />

          <Right>
            <NavItemDropdown>
              <NavItem
                {...resourcesDropdownProps}
                {...navItemProps}
                icon={<IconLearn />}
                isHoverActive={isHoveringResources}
                text={
                  <>
                    Resources <ChevronDown />
                  </>
                }
              />
              <AnimatePresence>
                {isHoveringResources && resourcesDropdown}
              </AnimatePresence>
            </NavItemDropdown>
            <NavItem
              {...navItemProps}
              icon={<IconPricing />}
              text="Pricing"
              to={INTERNAL_LINKS.Pricing}
            />
            <MiscButton
              {...miscButtonProps}
              alignText="center"
              link={LOGIN_LINK}
              stylez="minimal"
              text="Log in"
            />
            <MiscButton
              {...miscButtonProps}
              $getStarted={true}
              alignText="center"
              link={`${SIGNUP_LINK}?source=nav`}
              stylez="loud"
              text="Get started"
            />
          </Right>
        </CenteredNavOptions>
      );
    }

    return (
      <NavOptions>
        <Main>
          <NavItemDropdown>
            <NavItem
              {...productDropdownProps}
              {...navItemProps}
              icon={<IconProduct />}
              isHoverActive={isHoveringProduct}
              text={
                <>
                  Product <ChevronDown />
                </>
              }
            />
            <AnimatePresence>
              {isHoveringProduct && productDropdown}
            </AnimatePresence>
          </NavItemDropdown>
          <NavItemDropdown>
            <NavItem
              {...useCasesDropdownProps}
              {...navItemProps}
              icon={<IconUseCases />}
              isHoverActive={isHoveringUseCases}
              text={
                <>
                  Use cases <ChevronDown />
                </>
              }
            />
            <AnimatePresence>
              {isHoveringUseCases && useCasesDropdown}
            </AnimatePresence>
          </NavItemDropdown>
          <NavItem
            {...navItemProps}
            icon={<IconEnterprise />}
            text="Enterprise"
            to={INTERNAL_LINKS.Enterprise}
          />
          {centered && <NavLogo large={true} />}
          <NavItemDropdown>
            <NavItem
              {...resourcesDropdownProps}
              {...navItemProps}
              icon={<IconLearn />}
              isHoverActive={isHoveringResources}
              text={
                <>
                  Resources <ChevronDown />
                </>
              }
            />
            <AnimatePresence>
              {isHoveringResources && resourcesDropdown}
            </AnimatePresence>
          </NavItemDropdown>
          <NavItem
            {...navItemProps}
            icon={<IconPricing />}
            text="Pricing"
            to={INTERNAL_LINKS.Pricing}
          />
        </Main>
        <Misc>
          <MiscButton
            {...miscButtonProps}
            alignText="center"
            link={LOGIN_LINK}
            stylez="minimal"
            text="Log in"
          />
          <MiscButton
            {...miscButtonProps}
            $getStarted={true}
            alignText="center"
            link={`${SIGNUP_LINK}?source=nav`}
            stylez="loud"
            text="Get started"
          />
        </Misc>
      </NavOptions>
    );
  });
